import type { TEntityName } from '~/types/models'

/**
 * One user action, several kinds of write, one request.
 *
 * Changing a kit's options can supersede rows, revive hidden ones and create
 * new ones all at once. That used to go out as a PATCH followed by a separate
 * POST issued by the dialog, with no way to put the first back if the second
 * failed — see the header of buildMSServiceActionsForOptionChange. This payload
 * is what replaces the pair: a list of typed actions applied atomically.
 *
 * Every payload goes to `POST /api/transactions` (cynque-api-server #1627),
 * whatever entities it writes. The three per-collection
 * `/api/<silo>msservices/actions` routes it replaced are retired (#1627 phase
 * B), and with them the client-only `entityName` that used to pick between
 * the two.
 *
 * ⚠ THE TWO CLIENT-ONLY FIELDS NEVER GO ON THE WIRE. `__kind` is how
 * `postEntityData` and `patchEntityData` recognise the payload, and a
 * per-action `afterRequestCallback` lets the update half and the create half
 * notify different callers the way the two separate requests did.
 * `toEntityActionsWireBody` strips both. Do not rely on `JSON.stringify`
 * dropping the callback — it would, but it would happily serialise `__kind`,
 * and "it works because functions aren't JSON" is a coincidence rather than a
 * decision.
 */
export const ENTITY_ACTIONS_KIND = 'entityActions' as const

export type TEntityActionName = 'update' | 'create'

export interface IEntityAction {
  action: TEntityActionName
  /**
   * ⚠ Echoed on the wire and validated server-side. `/api/transactions` has
   * no collection in its URL, so this is the ONLY thing that says which list a
   * row belongs to. It must be registered there (the server's
   * `TRANSACTION_REGISTRY`, mirrored by `api/msw/modules/entityActionsRegistry.ts`)
   * or the whole request is a 422.
   */
  entity: TEntityName
  /**
   * ⚠ ORDER IS PART OF THE CONTRACT. The response is echoed positionally and
   * written back by index, so a reordered response puts versions on the wrong
   * rows. `postEntityData` passes the sent ids as `expectedIds` so that becomes
   * an immediate throw rather than silent corruption discovered later as a 409
   * on an unrelated save.
   */
  rows: any[]
  /** CLIENT-ONLY. Falls back to the dialog's own callback when absent. */
  afterRequestCallback?: Function
}

export interface IEntityActionsPayload {
  /** CLIENT-ONLY brand. */
  __kind: typeof ENTITY_ACTIONS_KIND
  actions: IEntityAction[]
}

/**
 * ⚠ A BRAND, NOT A DUCK-TYPE, on purpose. Detection runs on data that came out
 * of a dialog's `preRequestProcessor`, whose keys come from `inputFields` — an
 * entity that happened to have an `actions` array would be silently rerouted to
 * a different endpoint. One field to strip is a cheap price for that not being
 * possible.
 */
export function isEntityActionsPayload(
  value: any
): value is IEntityActionsPayload {
  return (
    !!value &&
    !Array.isArray(value) &&
    value.__kind === ENTITY_ACTIONS_KIND &&
    Array.isArray(value.actions)
  )
}

/** What actually goes on the wire: the client-only fields removed. */
export function toEntityActionsWireBody(payload: IEntityActionsPayload) {
  return {
    actions: payload.actions.map(({ action, entity, rows }) => ({
      action,
      entity,
      rows,
    })),
  }
}

/**
 * The response has to line up with the request before anything is written back,
 * because the write-back is positional and mutates the caller's own rows. A
 * mismatch caught here is a thrown error; a mismatch caught nowhere is versions
 * on the wrong entities.
 */
export function assertIsEntityActionsResponse(
  fn: string,
  data: any,
  request: IEntityActionsPayload
): asserts data is { actions: Array<{ rows: any[] }> } {
  if (!data || Array.isArray(data) || !Array.isArray(data.actions)) {
    throw new TypeError(`${fn} the response has no actions array.`)
  }
  if (data.actions.length !== request.actions.length) {
    throw new TypeError(
      `${fn} the response has ${data.actions.length} actions for` +
        ` ${request.actions.length} sent.`
    )
  }
  request.actions.forEach((sent, index) => {
    const returned = data.actions[index]
    if (returned?.action !== sent.action || returned?.entity !== sent.entity) {
      throw new TypeError(
        `${fn} action ${index} came back as` +
          ` '${returned?.action}'/'${returned?.entity}' but` +
          ` '${sent.action}'/'${sent.entity}' was sent.`
      )
    }
    if (
      !Array.isArray(returned.rows) ||
      returned.rows.length !== sent.rows.length
    ) {
      throw new TypeError(
        `${fn} action ${index} came back with ${returned?.rows?.length} rows` +
          ` for ${sent.rows.length} sent.`
      )
    }
  })
}
